// ===================================================================
// KAIXO - Cliente: traducción de foto (OCR + traducción)
// ===================================================================
// Multipart upload del blob/file de imagen al endpoint /api/photo-translate.
// Devuelve { hasText, sourceLanguage, original, translation }.
// ===================================================================

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';

export async function translatePhoto(imageFile, language) {
    const token = localStorage.getItem('kaixo_token');
    const fd = new FormData();
    fd.append('image', imageFile);
    if (language) fd.append('language', language);
    const { data } = await axios.post(
        `${API_URL}/photo-translate`,
        fd,
        {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                'Content-Type': 'multipart/form-data'
            },
            timeout: 60000
        }
    );
    return data;
}

/**
 * Versión streaming. Llama a la ruta SSE y va invocando los callbacks
 * con cada delta de texto que devuelve Claude, en lugar de esperar a
 * la respuesta completa. El frontend hace el parseo de los marcadores
 * <<<LANG>>>/<<<ORIGINAL>>>/<<<TRANSLATION>>> sobre el buffer acumulado.
 *
 * @param {File|Blob} imageFile
 * @param {string} language
 * @param {{ onDelta:(text:string)=>void, onDone:()=>void, onError:(err:Error)=>void, signal?:AbortSignal }} cb
 */
export async function translatePhotoStream(imageFile, language, { onDelta, onDone, onError, signal }) {
    const token = localStorage.getItem('kaixo_token');
    const fd = new FormData();
    fd.append('image', imageFile);
    if (language) fd.append('language', language);

    let resp;
    try {
        resp = await fetch(`${API_URL}/photo-translate/stream`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
            signal
        });
    } catch (err) {
        if (onError) onError(err);
        return;
    }
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        if (onError) onError(new Error(`HTTP ${resp.status}: ${text || resp.statusText}`));
        return;
    }
    if (!resp.body) {
        if (onError) onError(new Error('Streaming no soportado'));
        return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            // SSE separa eventos por blank line "\n\n"
            const events = buf.split('\n\n');
            buf = events.pop() ?? '';
            for (const raw of events) {
                if (!raw.trim()) continue;
                let evt = '';
                let data = '';
                for (const line of raw.split('\n')) {
                    if (line.startsWith('event:')) evt = line.slice(6).trim();
                    else if (line.startsWith('data:')) data += line.slice(5).trim();
                }
                if (!evt) continue;
                let parsed;
                try { parsed = JSON.parse(data || '{}'); } catch { parsed = {}; }
                if (evt === 'delta' && typeof parsed.text === 'string') {
                    if (onDelta) onDelta(parsed.text);
                } else if (evt === 'done') {
                    if (onDone) onDone();
                    return;
                } else if (evt === 'error') {
                    if (onError) onError(new Error(parsed.error || 'stream error'));
                    return;
                }
            }
        }
        if (onDone) onDone();
    } catch (err) {
        if (onError) onError(err);
    }
}

/**
 * Parsea el buffer acumulado de deltas y devuelve los bloques.
 * Útil para llamarlo en cada delta y refrescar la UI progresivamente.
 *
 * Tolerante a variaciones de Claude:
 *  - mayúsculas/minúsculas
 *  - espacios dentro de los marcadores: <<< LANG >>>
 *  - dos puntos opcional: <<<ORIGINAL>>>:
 *  - prefijo ```...``` que a veces añade
 */
export function parsePhotoStreamBuffer(text) {
    const out = { sourceLanguage: '', original: '', translation: '', done: false };
    if (!text) return out;

    // Quitar fences ```...``` si Claude las mete
    let t = text.replace(/^\s*```(?:json)?\s*\n?/i, '').replace(/```\s*$/i, '');

    const find = (label) => {
        const re = new RegExp('<<<\\s*' + label + '\\s*>>>:?', 'i');
        const m = t.match(re);
        return m ? { idx: m.index, len: m[0].length } : null;
    };

    const langPos = find('LANG');
    if (langPos) {
        const after = t.slice(langPos.idx + langPos.len);
        const newlineIdx = after.indexOf('\n');
        const langValue = newlineIdx === -1 ? after : after.slice(0, newlineIdx);
        out.sourceLanguage = langValue.trim();
    }

    const origPos = find('ORIGINAL');
    const transPos = find('TRANSLATION');
    const endPos = find('END');

    if (origPos) {
        const start = origPos.idx + origPos.len;
        const end = transPos ? transPos.idx : (endPos ? endPos.idx : t.length);
        out.original = t.slice(start, end).trim();
    }
    if (transPos) {
        const start = transPos.idx + transPos.len;
        const end = endPos ? endPos.idx : t.length;
        out.translation = t.slice(start, end).trim();
    }
    if (endPos) out.done = true;
    return out;
}
